import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Wrench,
  Flame,
  ShieldCheck,
  ClipboardList,
  Sun,
  Sunset,
  CalendarDays,
  Phone,
  MapPin,
  User,
  CheckCircle2,
  Camera,
} from "lucide-react";
import { SERVICE_OPTIONS, TIME_WINDOWS, COMPANY } from "@/lib/site";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = {
  "boiler-service": Flame,
  "emergency-repair": Wrench,
  "landlord-check": ShieldCheck,
  "general-quote": ClipboardList,
};

const todayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1); // earliest = tomorrow
  return d.toISOString().slice(0, 10);
};

const Stepper = ({ step }) => (
  <ol className="flex items-center w-full mb-12" data-testid="booking-stepper">
    {["Service", "Date & Window", "Your details"].map((label, i) => {
      const n = i + 1;
      const active = n === step;
      const done = n < step;
      return (
        <li
          key={label}
          className={`flex-1 flex items-center ${i < 2 ? "after:content-[''] after:flex-1 after:h-0.5 after:ml-4 after:bg-slate-200" : ""}`}
        >
          <span
            className={`flex items-center justify-center w-10 h-10 rounded-sm text-sm font-bold shrink-0 ${
              active
                ? "bg-brand-orange text-white"
                : done
                ? "bg-brand-navy text-white"
                : "bg-slate-100 text-slate-400 border border-slate-200"
            }`}
          >
            {done ? <Check className="w-5 h-5" /> : String(n).padStart(2, "0")}
          </span>
          <span className={`ml-3 hidden sm:block text-sm font-bold uppercase tracking-wide ${active ? "text-brand-navy" : done ? "text-slate-700" : "text-slate-400"}`}>
            {label}
          </span>
        </li>
      );
    })}
  </ol>
);

export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [form, setForm] = useState({
    service_type: "",
    preferred_date: "",
    time_window: "",
    name: "",
    phone: "",
    address: "",
    notes: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canNext1 = !!form.service_type;
  const canNext2 = !!form.preferred_date && !!form.time_window;
  const canSubmit = form.name.length >= 2 && form.phone.length >= 6 && form.address.length >= 4;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please fill in all required details.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/bookings`, form);
      setConfirmed(data);
      toast.success("Booking received — we'll call you to confirm shortly.");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Could not submit booking. Please call us.";
      toast.error(typeof msg === "string" ? msg : "Could not submit booking. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <section className="py-24 bg-slate-50 min-h-[70vh]" data-testid="booking-confirmation">
        <div className="container-x max-w-2xl">
          <div className="bg-white border border-slate-200 rounded-sm p-10 shadow-sm">
            <div className="grid place-items-center w-16 h-16 bg-green-50 border border-green-200 rounded-sm">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="mt-6 font-heading font-black text-4xl tracking-tighter text-brand-navy">
              Booking received.
            </h1>
            <p className="mt-3 text-slate-700">
              Thanks {confirmed.name}. Your reference is{" "}
              <span className="font-mono font-bold text-brand-navy">{confirmed.id.slice(0, 8).toUpperCase()}</span>.
              One of our engineers will call <strong>{confirmed.phone}</strong> within the hour to confirm
              the slot.
            </p>

            <div
              className="mt-6 flex items-start gap-3 p-4 bg-orange-50 border-l-4 border-brand-orange rounded-sm"
              data-testid="booking-reference-reminder"
            >
              <Camera className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" />
              <p className="text-sm text-slate-800 leading-relaxed">
                <span className="font-bold text-brand-navy">Quick reminder:</span> take a screenshot of
                your reference number{" "}
                <span className="font-mono font-bold text-brand-navy">
                  {confirmed.id.slice(0, 8).toUpperCase()}
                </span>{" "}
                — or jot it down somewhere safe — so you have it handy when we call you back.
              </p>
            </div>
            <dl className="mt-7 divide-y divide-slate-200 border border-slate-200 rounded-sm">
              {[
                ["Service", SERVICE_OPTIONS.find((s) => s.value === confirmed.service_type)?.label],
                ["Date", confirmed.preferred_date],
                ["Window", TIME_WINDOWS.find((t) => t.value === confirmed.time_window)?.range],
                ["Address", confirmed.address],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
                  <dt className="font-bold uppercase tracking-wide text-slate-500">{k}</dt>
                  <dd className="font-semibold text-slate-900 text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-primary" data-testid="confirmation-call-button">
                <Phone className="w-5 h-5" /> Call us: {COMPANY.phone}
              </a>
              <button
                onClick={() => {
                  setConfirmed(null);
                  setStep(1);
                  setForm({
                    service_type: "",
                    preferred_date: "",
                    time_window: "",
                    name: "",
                    phone: "",
                    address: "",
                    notes: "",
                  });
                }}
                className="btn-outline-navy"
                data-testid="confirmation-new-booking-button"
              >
                Book another job
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <Seo
        title="Book an Engineer Online | EL-Gas and Plumbing — Hackney & Islington"
        description="Book a Gas Safe plumber in 60 seconds. Pick your service, date and preferred window — we call back to confirm within the hour."
        path="/booking"
      />
      <section className="bg-brand-navy text-white py-16" data-testid="booking-hero">
        <div className="container-x">
          <span className="overline text-brand-orange">Booking</span>
          <h1 className="mt-3 font-heading font-black text-5xl md:text-6xl tracking-tighter">
            Schedule your engineer slot in <span className="text-brand-orange">60 seconds.</span>
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Three quick steps. Our team will phone you back within the hour to confirm the slot and
            quote.
          </p>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="booking-form-section">
        <div className="container-x max-w-3xl">
          <div className="bg-white border border-slate-200 rounded-sm shadow-md p-8 md:p-10">
            <Stepper step={step} />

            {/* STEP 1 — Service */}
            {step === 1 && (
              <div data-testid="booking-step-1">
                <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight text-brand-navy">
                  What can we help with?
                </h2>
                <p className="text-slate-600 mt-2 text-sm">Pick the service that fits best — we’ll dial it in later.</p>

                <div className="mt-7 grid sm:grid-cols-2 gap-4">
                  {SERVICE_OPTIONS.map((s) => {
                    const Icon = SERVICE_ICONS[s.value];
                    const selected = form.service_type === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => update("service_type", s.value)}
                        data-testid={`booking-service-${s.value}`}
                        className={`text-left p-5 border-2 rounded-sm transition-all ${
                          selected
                            ? "border-brand-orange bg-orange-50 shadow-md"
                            : "border-slate-200 bg-white hover:border-brand-navy"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`grid place-items-center w-10 h-10 rounded-sm ${selected ? "bg-brand-orange text-white" : "bg-brand-navy text-white"}`}>
                            <Icon className="w-5 h-5" />
                          </span>
                          {selected && <Check className="w-5 h-5 text-brand-orange" />}
                        </div>
                        <p className="mt-4 font-heading font-bold text-lg text-brand-navy">{s.label}</p>
                        <p className="text-sm text-slate-600 mt-1">{s.description}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-9 flex justify-end">
                  <button
                    disabled={!canNext1}
                    onClick={() => setStep(2)}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="booking-step-1-next"
                  >
                    Continue <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2 — Date & Window */}
            {step === 2 && (
              <div data-testid="booking-step-2">
                <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight text-brand-navy">
                  Pick a date &amp; window.
                </h2>
                <p className="text-slate-600 mt-2 text-sm">Same-day available — earliest dispatch tomorrow online.</p>

                <div className="mt-7 space-y-6">
                  <div>
                    <label className="overline mb-2 inline-flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" /> Preferred date
                    </label>
                    <input
                      type="date"
                      min={todayIso()}
                      value={form.preferred_date}
                      onChange={(e) => update("preferred_date", e.target.value)}
                      data-testid="booking-date-input"
                      className="w-full mt-1 px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>

                  <div>
                    <p className="overline mb-3">Preferred window</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {TIME_WINDOWS.map((t) => {
                        const Icon = t.value === "morning" ? Sun : Sunset;
                        const selected = form.time_window === t.value;
                        return (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => update("time_window", t.value)}
                            data-testid={`booking-window-${t.value}`}
                            className={`text-left p-5 border-2 rounded-sm transition-all ${
                              selected
                                ? "border-brand-orange bg-orange-50 shadow-md"
                                : "border-slate-200 hover:border-brand-navy"
                            }`}
                          >
                            <Icon className={`w-7 h-7 ${selected ? "text-brand-orange" : "text-brand-navy"}`} />
                            <p className="mt-3 font-heading font-bold text-lg text-brand-navy">{t.label}</p>
                            <p className="text-sm text-slate-600">{t.range}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="mt-9 flex justify-between gap-3">
                  <button onClick={() => setStep(1)} className="btn-outline-navy" data-testid="booking-step-2-back">
                    <ArrowLeft className="w-5 h-5" /> Back
                  </button>
                  <button
                    disabled={!canNext2}
                    onClick={() => setStep(3)}
                    className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="booking-step-2-next"
                  >
                    Continue <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3 — Contact */}
            {step === 3 && (
              <div data-testid="booking-step-3">
                <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight text-brand-navy">
                  Your contact details.
                </h2>
                <p className="text-slate-600 mt-2 text-sm">We’ll call you within the hour to confirm.</p>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-1">
                    <label className="overline inline-flex items-center gap-2 mb-2"><User className="w-4 h-4"/> Full name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Jane Smith"
                      data-testid="booking-name-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div>
                    <label className="overline inline-flex items-center gap-2 mb-2"><Phone className="w-4 h-4"/> Phone number</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="07xxx xxxxxx"
                      data-testid="booking-phone-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline inline-flex items-center gap-2 mb-2"><MapPin className="w-4 h-4"/> Address in E5 / E8 / E9 / N1 / N5</label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                      placeholder="Flat 4, 12 Median Rd, Lower Clapton, London E5 0PL"
                      data-testid="booking-address-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline mb-2 block">Notes (optional)</label>
                    <textarea
                      rows={4}
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                      placeholder="Boiler model, what's happening, access notes…"
                      data-testid="booking-notes-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-medium"
                    />
                  </div>
                </div>

                <div className="mt-9 flex justify-between gap-3">
                  <button onClick={() => setStep(2)} className="btn-outline-navy" data-testid="booking-step-3-back">
                    <ArrowLeft className="w-5 h-5" /> Back
                  </button>
                  <button
                    onClick={submit}
                    disabled={!canSubmit || submitting}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="booking-submit-button"
                  >
                    {submitting ? "Submitting…" : "Confirm booking"}
                    {!submitting && <ArrowRight className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-slate-600">
            Or call us directly:{" "}
            <a href={`tel:${COMPANY.phoneTel}`} className="font-bold text-brand-navy hover:text-brand-orange">
              {COMPANY.phone}
            </a>
          </p>
        </div>
      </section>
    </>
  );
}
